import { AvailableLangs, Translation, TranslocoEvents, TranslocoService } from '@ngneat/transloco';
import { Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';

import { TranslationKey } from '../types/translation-keys.type';

export interface TranslationParams {
  [key: string]: string | number;
}

export interface TranslatedFieldOptions {
  label: string;
  placeholder?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  constructor(private readonly _transloco: TranslocoService) {}

  translate(key: TranslationKey, params?: TranslationParams): string {
    try {
      const result = this._transloco.translate(key, params);
      return result !== undefined ? result : key;
    } catch (error) {
      console.error(`Translation error for key ${key}:`, error);
      return key;
    }
  }

  setTitle(title: TranslationKey): Observable<string> {
    return this._transloco.selectTranslate(title);
  }


  get events$(): Observable<TranslocoEvents> {
    return this._transloco.events$;
  }
  
  getActiveLang(): string {
    return this._transloco.getActiveLang();
  }

  getAvailableLangs(): AvailableLangs {
    return this._transloco.getAvailableLangs();
  }

  getTranslation(): Translation {
    const lang = this._transloco.getActiveLang();
    return this._transloco.getTranslation(lang);
  }

  createTranslatedFieldOptions(labelKey: TranslationKey, placeholderKey?: TranslationKey, additionalOptions: Record<string, unknown> = {}): TranslatedFieldOptions {
    const translatedLabel = this.translate(labelKey);
    const translatedPlaceholder = placeholderKey ? this.translate(placeholderKey) : undefined;
    
    const options: TranslatedFieldOptions = {
      label: translatedLabel,
      ...additionalOptions
    };

    if (placeholderKey) {
      options.placeholder = translatedPlaceholder;
    }

    return options;
  }

  setActiveLang(language: string): void {
    this._transloco.setActiveLang(language);
  }

  public initialize(defaultLang: string = 'en-US'): void {
    const currentLang = this.getActiveLang();
    if (currentLang) {
      this.setActiveLang(currentLang);
    } else {
      this.setActiveLang(defaultLang);
    }
  }

  translationReady$(): Observable<boolean> {
    const translations = this.getTranslation();
    if (translations && Object.keys(translations).length > 0) {
      return of(true);
    }
    
    return this.events$.pipe(
      filter((event) => event.type === 'translationLoadSuccess'),
      switchMap(() => of(true))
    );
  }

  translateWeekday(weekdayKey: string): string {
    const weekdayTranslationMap: Record<string, TranslationKey> = {
      'Monday': 'common.daysOfWeek.monday' as TranslationKey,
      'Tuesday': 'common.daysOfWeek.tuesday' as TranslationKey,
      'Wednesday': 'common.daysOfWeek.wednesday' as TranslationKey,
      'Thursday': 'common.daysOfWeek.thursday' as TranslationKey,
      'Friday': 'common.daysOfWeek.friday' as TranslationKey,
      'Saturday': 'common.daysOfWeek.saturday' as TranslationKey,
      'Sunday': 'common.daysOfWeek.sunday' as TranslationKey
    };

    const translationKey = weekdayTranslationMap[weekdayKey];
    return translationKey ? this.translate(translationKey) : weekdayKey;
  }

  createTranslatedPaginatorIntl(): MatPaginatorIntl {
    const paginatorIntl = new MatPaginatorIntl();
    
    paginatorIntl.itemsPerPageLabel = this.translate('common.itemsPerPage');
    paginatorIntl.nextPageLabel = this.translate('common.nextPage');
    paginatorIntl.previousPageLabel = this.translate('common.previousPage');
    paginatorIntl.firstPageLabel = this.translate('common.firstPage');
    paginatorIntl.lastPageLabel = this.translate('common.lastPage');
    
    paginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number) => {
      if (length === 0 || pageSize === 0) {
        return this.translate('common.rangePageLabel', { length: 0 });
      }
      const startIndex = page * pageSize;
      const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
      return this.translate('common.rangePageLabel', {
        startIndex : startIndex + 1,
        endIndex : endIndex,
        length : length,
      });
    };

    return paginatorIntl;
  }
   
  translateTimeAgo(date: Date | number): string {
    const now = Date.now();
    const dateValue = typeof date === 'number' ? date : date.valueOf();
    const diffInSeconds = Math.floor((now - dateValue) / 1000);
    
    if (diffInSeconds < 60) {
      return this.translate('common.timeAgo.justNow');
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      const key = minutes === 1 ? 'common.timeAgo.minuteAgo' : 'common.timeAgo.minutesAgo';
      return this.translate(key, { count: minutes });
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      const key = hours === 1 ? 'common.timeAgo.hourAgo' : 'common.timeAgo.hoursAgo';
      return this.translate(key, { count: hours });
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      const key = days === 1 ? 'common.timeAgo.dayAgo' : 'common.timeAgo.daysAgo';
      return this.translate(key, { count: days });
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      const key = months === 1 ? 'common.timeAgo.monthAgo' : 'common.timeAgo.monthsAgo';
      return this.translate(key, { count: months });
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      const key = years === 1 ? 'common.timeAgo.yearAgo' : 'common.timeAgo.yearsAgo';
      return this.translate(key, { count: years });
    }
  }
} 
